import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  GetUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { cdkConfig } from '../utils/cdkConfig';

// Create Cognito client
const cognitoClient = new CognitoIdentityProviderClient({
  region: cdkConfig.region,
});

export interface User {
  username: string;
  email?: string;
  attributes?: Record<string, string>;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ nextStep: any }>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, confirmationCode: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated on app start
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (token) {
        // Verify token is still valid by getting user info
        const getUserCommand = new GetUserCommand({
          AccessToken: token,
        });
        
        const userData = await cognitoClient.send(getUserCommand);
        
        setUser({
          username: userData.Username || '',
          email: userData.UserAttributes?.find((attr: any) => attr.Name === 'email')?.Value,
          attributes: userData.UserAttributes?.reduce((acc: any, attr: any) => {
            if (attr.Name && attr.Value) {
              acc[attr.Name] = attr.Value;
            }
            return acc;
          }, {} as Record<string, string>),
        });
      } else {
        setUser(null);
      }
    } catch (err) {
      // Token is invalid or expired, clear it
      localStorage.removeItem('accessToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: cdkConfig.cognito.userPoolClientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);
      
      if (response.AuthenticationResult?.AccessToken) {
        const token = response.AuthenticationResult.AccessToken;
        localStorage.setItem('accessToken', token);
        await checkAuthState(); // Refresh user data
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const command = new SignUpCommand({
        ClientId: cdkConfig.cognito.userPoolClientId,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
          { Name: 'given_name', Value: firstName },
          { Name: 'family_name', Value: lastName },
        ],
      });

      const result = await cognitoClient.send(command);
      
      return { 
        nextStep: { 
          signUpStep: result.UserConfirmed ? 'DONE' : 'CONFIRM_SIGN_UP' 
        } 
      };
    } catch (err: any) {
      setError(err.message || 'Failed to sign up. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear local storage and state
      localStorage.removeItem('accessToken');
      setUser(null);
    } catch (err: any) {
      setError(err.message || 'Failed to sign out.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (email: string, confirmationCode: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const command = new ConfirmSignUpCommand({
        ClientId: cdkConfig.cognito.userPoolClientId,
        Username: email,
        ConfirmationCode: confirmationCode,
      });

      await cognitoClient.send(command);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm sign up.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmationCode = async (email: string) => {
    try {
      setError(null);
      
      const command = new ResendConfirmationCodeCommand({
        ClientId: cdkConfig.cognito.userPoolClientId,
        Username: email,
      });

      await cognitoClient.send(command);
    } catch (err: any) {
      setError(err.message || 'Failed to resend confirmation code.');
      throw err;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    confirmSignUp: handleConfirmSignUp,
    resendConfirmationCode: handleResendConfirmationCode,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}